/* Modulo Trust Reviews — carousel + view-more toggle.
   Vanilla IIFE, lazy-init, reduced-motion aware. */
(function () {
  'use strict';
  if (typeof window === 'undefined') return;

  var REDUCE = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function clamp(n, min, max) { return Math.max(min, Math.min(max, n)); }

  function initSection(el) {
    var track = el.querySelector('[data-modulo-trv-track]');
    if (!track) return;

    var prev    = el.querySelector('[data-modulo-trv-prev]');
    var next    = el.querySelector('[data-modulo-trv-next]');
    var dots    = el.querySelector('[data-modulo-trv-dots]');
    var cards   = function () { return track.querySelectorAll('[data-modulo-trv-card]'); };
    var trunc   = parseInt(el.getAttribute('data-truncation-lines'), 10);
    if (!trunc || isNaN(trunc)) trunc = 4;
    el.style.setProperty('--modulo-trv-truncation-lines', trunc);

    var moreLabel = el.getAttribute('data-view-more') || 'View more';
    var lessLabel = el.getAttribute('data-view-less') || 'View less';
    var stepMode  = el.getAttribute('data-step-mode') === 'single' ? 'single' : 'page';

    // ─── View More toggles + truncation detection ───
    function detectTruncation() {
      Array.prototype.forEach.call(cards(), function (card) {
        var body = card.querySelector('[data-modulo-trv-body]');
        if (!body) return;
        // Reset expanded state before measuring so detection is accurate
        var wasExpanded = card.classList.contains('is-expanded');
        if (wasExpanded) card.classList.remove('is-expanded');
        if (body.scrollHeight - body.clientHeight > 1) {
          card.classList.add('is-truncated');
        } else {
          card.classList.remove('is-truncated');
        }
        if (wasExpanded) card.classList.add('is-expanded');
      });
    }

    function wireToggles() {
      Array.prototype.forEach.call(cards(), function (card) {
        var toggle = card.querySelector('[data-modulo-trv-toggle]');
        var label  = card.querySelector('[data-modulo-trv-toggle-label]');
        if (!toggle || toggle.dataset.bound === '1') return;
        toggle.dataset.bound = '1';
        toggle.addEventListener('click', function () {
          var expanded = card.classList.toggle('is-expanded');
          toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
          if (label) label.textContent = expanded ? lessLabel : moreLabel;
        });
      });
    }

    // ─── Carousel paging ───
    function viewportWidth() { return window.innerWidth || document.documentElement.clientWidth; }

    function cardsPerView() {
      var w = viewportWidth();
      var attr;
      if (w <= 480) attr = 'data-cpv-mobile';
      else if (w <= 860) attr = 'data-cpv-tablet';
      else attr = 'data-cpv-desktop';
      var n = parseInt(el.getAttribute(attr), 10);
      return n && !isNaN(n) ? n : 3;
    }

    var page = 0;

    function applyCpv() {
      var cpv = cardsPerView();
      el.style.setProperty('--modulo-trv-cpv', cpv);
    }

    // In single mode each "page" is one card; the last reachable index is total-cpv
    // so the final viewport always shows full cards (no half-empty trailing slot).
    function pageCount() {
      var total = cards().length;
      var cpv = cardsPerView();
      if (stepMode === 'single') return Math.max(1, total - cpv + 1);
      return Math.max(1, Math.ceil(total / cpv));
    }

    function goTo(p, animate) {
      var pages = pageCount();
      page = clamp(p, 0, pages - 1);
      var cpv = cardsPerView();
      var firstCard = cards()[0];
      if (!firstCard) return;
      var cardW = firstCard.getBoundingClientRect().width;
      var gap = parseFloat(getComputedStyle(el).getPropertyValue('--modulo-trv-gap')) || 20;
      // page advances by 1 card in single mode, by cpv cards in page mode
      var stride = stepMode === 'single' ? 1 : cpv;
      var offset = page * stride * (cardW + gap);
      if (!animate || REDUCE) {
        var prevTransition = track.style.transition;
        track.style.transition = 'none';
        track.style.transform = 'translate3d(' + (-offset) + 'px, 0, 0)';
        // Force reflow then restore transition
        track.offsetHeight;
        track.style.transition = prevTransition;
      } else {
        track.style.transform = 'translate3d(' + (-offset) + 'px, 0, 0)';
      }
      if (prev) prev.disabled = page === 0;
      if (next) next.disabled = page === pages - 1;
      renderDots();
    }

    function renderDots() {
      if (!dots) return;
      while (dots.firstChild) dots.removeChild(dots.firstChild);
      var pages = pageCount();
      for (var i = 0; i < pages; i++) {
        (function (idx) {
          var dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'modulo-trv__dot' + (idx === page ? ' is-active' : '');
          dot.setAttribute('role', 'tab');
          dot.setAttribute('aria-label', 'Page ' + (idx + 1));
          if (idx === page) dot.setAttribute('aria-selected', 'true');
          dot.addEventListener('click', function () {
            stopAutoplay();
            goTo(idx, true);
          });
          dots.appendChild(dot);
        })(i);
      }
    }

    if (prev) prev.addEventListener('click', function () { stopAutoplay(); goTo(page - 1, true); });
    if (next) next.addEventListener('click', function () { stopAutoplay(); goTo(page + 1, true); });

    // ─── Touch swipe ───
    var SWIPE_THRESHOLD = 40;
    var startX = null;
    var startY = null;
    track.addEventListener('pointerdown', function (e) {
      if (e.pointerType !== 'touch' && e.pointerType !== 'pen') return;
      startX = e.clientX;
      startY = e.clientY;
    });
    track.addEventListener('pointerup', function (e) {
      if (startX === null) return;
      var dx = e.clientX - startX;
      var dy = e.clientY - startY;
      if (Math.abs(dx) > SWIPE_THRESHOLD && Math.abs(dx) > Math.abs(dy)) {
        stopAutoplay();
        if (dx < 0) goTo(page + 1, true);
        else        goTo(page - 1, true);
      }
      startX = null; startY = null;
    });
    track.addEventListener('pointercancel', function () { startX = null; startY = null; });

    // ─── Autoplay ───
    var autoplayTimer = null;
    var autoplayEnabled = el.getAttribute('data-autoplay') === 'true' && !REDUCE;
    var autoplaySpeedSec = parseFloat(el.getAttribute('data-autoplay-speed'));
    if (!autoplaySpeedSec || isNaN(autoplaySpeedSec)) autoplaySpeedSec = 6;

    function startAutoplay() {
      if (!autoplayEnabled) return;
      if (autoplayTimer) return;
      autoplayTimer = window.setInterval(function () {
        var pages = pageCount();
        var nextPage = page + 1 >= pages ? 0 : page + 1;
        goTo(nextPage, true);
      }, autoplaySpeedSec * 1000);
    }
    function stopAutoplay() {
      if (autoplayTimer) {
        window.clearInterval(autoplayTimer);
        autoplayTimer = null;
      }
      autoplayEnabled = false;
    }
    el.addEventListener('mouseenter', function () { if (autoplayTimer) { window.clearInterval(autoplayTimer); autoplayTimer = null; } });
    el.addEventListener('mouseleave', function () { startAutoplay(); });

    // ─── Resize ───
    var resizeT;
    window.addEventListener('resize', function () {
      window.clearTimeout(resizeT);
      resizeT = window.setTimeout(function () {
        applyCpv();
        // After CPV changes, re-layout: re-detect truncation and snap to page 0
        requestAnimationFrame(function () {
          detectTruncation();
          goTo(0, false);
        });
      }, 100);
    });

    // ─── Boot ───
    applyCpv();
    requestAnimationFrame(function () {
      detectTruncation();
      wireToggles();
      goTo(0, false);
      startAutoplay();
    });
  }

  function bootstrap() {
    var sections = document.querySelectorAll('[data-modulo-trv]');
    if (window.moduloLazyInit) {
      window.moduloLazyInit('[data-modulo-trv]', initSection);
    } else {
      Array.prototype.forEach.call(sections, initSection);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }
})();
